/**
 * Single source of truth for the conference dates shown across the public site.
 *
 * These built-in defaults render everywhere until an admin customises them in
 * Admin → Conference Dates, which stores an override in the `settings` content
 * block. The override is fetched once at app start (see ConferenceConfigContext)
 * and merged over these defaults, so changing the dates in the admin panel
 * updates the hero, hybrid banner, footer, agenda, CFP page and SEO metadata
 * everywhere at once.
 */

export const CONFERENCE_DATES_DEFAULTS = {
  // ISO dates — used for SEO structured data (schema.org Event).
  startDate: '2026-09-17',
  endDate: '2026-09-19',
  // Human-readable range shown throughout the site.
  displayRange: '17–19 September 2026',
  // Location label paired with the date range.
  cityCountry: 'Bangkok, Thailand',
  // Hybrid-mode breakdown.
  physicalDate: '17 September 2026',
  digitalDates: '18–19 September 2026',
  // Per-day participation mode for the Participation Form date options,
  // keyed by ISO date → 'both' | 'physical' | 'digital'. When a day is not
  // listed the backend defaults the first conference day to hybrid (both) and
  // every later day to digital-only. Edit in Admin → Conference Dates.
  dayModes: {
    '2026-09-17': 'both',
    '2026-09-18': 'digital',
    '2026-09-19': 'digital',
  },
  // The black banner sentence on the home page.
  hybridNote:
    'This Fifth Edition of the conference will be in Hybrid Mode — 17 September 2026 (Physical & Digital Mode) · 18–19 September 2026 (Digital Mode)',
};

/** Compose the shared SEO/meta description from the current dates. */
export function conferenceSeoDescription(dates) {
  const d = dates ?? CONFERENCE_DATES_DEFAULTS;
  return `5th World Conference on Information Systems for Business Management — ISBM 2026, ${d.cityCountry}, ${d.displayRange}.`;
}

/**
 * Keep any "Conference Dates" row in an important-dates list in sync with the
 * admin Conference Dates display range (Physical/Digital source of truth).
 */
export function syncImportantDatesWithConference(items, dates) {
  const list = Array.isArray(items) ? items : [];
  const displayRange = (dates ?? CONFERENCE_DATES_DEFAULTS).displayRange;
  if (!displayRange) return list;
  return list.map((item) =>
    item && typeof item.label === 'string' && /^conference dates$/i.test(item.label.trim())
      ? { ...item, date: displayRange }
      : item,
  );
}

/** Short city label from "City, Country" for chips and compact UI. */
export function conferenceCityShort(dates) {
  const cityCountry = (dates ?? CONFERENCE_DATES_DEFAULTS).cityCountry || '';
  return cityCountry.split(',')[0].trim() || cityCountry;
}
